import {
  getBusinessRelationshipRowKey,
  type BusinessRelationship,
  type BusinessRelationshipSort,
} from '@/entities/business-relationship/api/business-relationship.api';
import { useBusinessRelationshipList } from '@/entities/business-relationship/hooks/useBusinessRelationships';
import { TradeStatusBadge } from '@/entities/business-relationship/ui/TradeStatusBadge';
import {
  DrugName,
  RegistrationDetailPanel,
} from '@/entities/prescription-registration/ui/RegistrationDetailPanel';
import {
  DEFAULT_VIEW_PAGE_SIZE,
  PAGE_SIZE_OPTIONS,
} from '@/shared/constants/pagination';
import { formatCount, orDash } from '@/shared/lib/format';
import {
  Badge,
  Card,
  DotSeparated,
  FilterButton,
  LabeledValue,
  ListState,
  ListToolbar,
  PageHeader,
  PageSizeSelect,
  Pagination,
  SearchInput,
  Select,
  TOOLBAR_SELECT_CLASS,
} from '@/shared/ui';
import { getRouteApi } from '@tanstack/react-router';
import { useCallback, useState } from 'react';
import { ClientsFilterDrawer, type ClientsFilter } from './ClientsFilterDrawer';
import type { ClientsSearch } from './clientsSearch';

const routeApi = getRouteApi('/partner/clients');

/** 정렬 (RDL-112) — 기본 담당자순 */
const SORT_OPTIONS: { value: BusinessRelationshipSort; label: string }[] = [
  { value: 'USER', label: '담당자순' },
  { value: 'CLINIC', label: '병의원명순' },
  { value: 'REGION', label: '지역순' },
  { value: 'PHARMA_COMPANY', label: '제약사순' },
];
const DEFAULT_SORT: BusinessRelationshipSort = 'USER';

const isClientsSort = (
  value: string | undefined,
): value is BusinessRelationshipSort =>
  SORT_OPTIONS.some((option) => option.value === value);

/**
 * 06 거래처 정보 — 제약사·의약품 거래를 카드 목록으로. 1카드 = 1거래. 조회 전용.
 * 필터는 오른쪽 필터 드로어, 카드 클릭 → 07-1 상세 패널(병의원 기준, 최근 처방 등록 월 강조).
 */
export function ClientsPage() {
  const search = routeApi.useSearch();
  const navigate = routeApi.useNavigate();
  const page = search.page ?? 1;
  const pageSize = search.size ?? DEFAULT_VIEW_PAGE_SIZE;
  const [isFilterOpen, setFilterOpen] = useState(false);

  const { data, isLoading, isFetching, error } = useBusinessRelationshipList({
    keyword: search.keyword,
    userIds: search.userIds,
    pharmaCompanyIds: search.pharmaCompanyIds,
    // 없으면 서버 기본값 ALL(전체)
    tradeStatus: search.tradeStatus,
    sort: search.sort,
    page: page - 1,
    size: pageSize,
  });

  const updateSearch = (patch: Partial<ClientsSearch>) =>
    navigate({ search: (prev) => ({ ...prev, ...patch, page: undefined }) });

  const filter: ClientsFilter = {
    userIds: search.userIds,
    pharmaCompanyIds: search.pharmaCompanyIds,
    tradeStatus: search.tradeStatus,
  };
  const isFilterActive = Object.values(filter).some(
    (value) => value !== undefined,
  );

  const openDetail = (row: BusinessRelationship) =>
    navigate({
      search: (prev) => ({
        ...prev,
        detailUserId: row.userId,
        detailClinicId: row.clinicId,
      }),
    });

  const closeDetail = useCallback(
    () =>
      navigate({
        search: (prev) => ({
          ...prev,
          detailUserId: undefined,
          detailClinicId: undefined,
        }),
      }),
    [navigate],
  );

  return (
    <div className="flex w-full flex-col items-center gap-6">
      <PageHeader
        title="거래처 정보"
        description="우리 업체 사용자가 거래하는 병의원과 제약사·의약품이에요."
      />

      <div className="flex w-full flex-col gap-4">
        <ListToolbar
          // 이전 결과를 보여 주며 새 조건으로 다시 불러오는 중
          isRefreshing={isFetching && !isLoading}
          summary={
            <span className="text-body-16 font-medium whitespace-nowrap text-fg-secondary">
              총 {formatCount(data?.totalCount ?? 0, '건')}
            </span>
          }
          controls={
            <>
              <Select
                label="정렬"
                allLabel={null}
                value={search.sort ?? DEFAULT_SORT}
                options={SORT_OPTIONS}
                onChange={(sort) =>
                  updateSearch({
                    // 기본값은 URL에 남기지 않는다
                    sort:
                      isClientsSort(sort) && sort !== DEFAULT_SORT
                        ? sort
                        : undefined,
                  })
                }
                className={TOOLBAR_SELECT_CLASS}
              />
              <PageSizeSelect
                value={pageSize}
                options={PAGE_SIZE_OPTIONS}
                onChange={(size) =>
                  // 기본값은 URL에 남기지 않는다. 개수가 바뀌면 1페이지로
                  updateSearch({
                    size: size === DEFAULT_VIEW_PAGE_SIZE ? undefined : size,
                  })
                }
              />
              <FilterButton
                active={isFilterActive}
                onClick={() => setFilterOpen(true)}
              />
            </>
          }
          search={
            <SearchInput
              value={search.keyword}
              placeholder="병의원명, 요양기관번호, 의약품"
              onSearch={(keyword) => updateSearch({ keyword })}
            />
          }
        />

        <ListState
          isLoading={isLoading}
          error={error}
          isEmpty={data?.content.length === 0}
        />
        <ul className="flex w-full flex-col gap-2">
          {data?.content.map((row) => (
            <li key={getBusinessRelationshipRowKey(row)}>
              <ClientCard
                row={row}
                onOpen={() => openDetail(row)}
                onSelectUser={() => updateSearch({ userIds: [row.userId] })}
              />
            </li>
          ))}
        </ul>
      </div>

      {data && data.totalCount > 0 && (
        <Pagination
          page={page}
          pageSize={pageSize}
          totalCount={data.totalCount}
          onChange={(nextPage) =>
            navigate({ search: (prev) => ({ ...prev, page: nextPage }) })
          }
        />
      )}

      <ClientsFilterDrawer
        open={isFilterOpen}
        value={filter}
        onClose={() => setFilterOpen(false)}
        onApply={(next) => {
          updateSearch(next);
          setFilterOpen(false);
        }}
      />

      {/* 06에서 열면 기준 월은 서버 기본값(이미지가 있는 최근 월) */}
      <RegistrationDetailPanel
        params={
          search.detailUserId && search.detailClinicId
            ? { userId: search.detailUserId, clinicId: search.detailClinicId }
            : null
        }
        onClose={closeDetail}
      />
    </div>
  );
}

/** 값이 길면 단어 단위로 줄바꿈하고, 공백 없는 긴 값은 아무 데서나 끊는다 */
const WRAP_VALUE_CLASS =
  'whitespace-normal break-keep [overflow-wrap:anywhere]';

interface ClientCardProps {
  row: BusinessRelationship;
  onOpen: () => void;
  /** 담당자 클릭 = 담당자 필터 (카드 클릭과 분리) */
  onSelectUser: () => void;
}

/** Figma card/거래처 — 병의원 · 제약사/의약품 · 담당자 */
function ClientCard({ row, onOpen, onSelectUser }: ClientCardProps) {
  return (
    <Card
      onPress={onOpen}
      className="flex-col gap-3 xl:flex-row xl:items-center xl:gap-4"
    >
      {/* 병의원·제약사 열은 고정 폭 — 남는 폭은 의약품이 가장 넓게 쓴다 */}
      <div className="flex w-full min-w-0 flex-col justify-center gap-2 xl:w-[340px] xl:shrink-0">
        <div className="flex min-w-0 items-center gap-2">
          <p className="truncate text-subtitle-18 font-bold text-fg-primary">
            {orDash(row.clinicName)}
          </p>
          <TradeStatusBadge {...row} />
          {row.clinicClosedOn && <Badge tone="error">폐업</Badge>}
        </div>
        <DotSeparated
          className="text-body-14 font-medium whitespace-nowrap text-fg-secondary"
          items={[orDash(row.ykiho), orDash(row.region)]}
        />
      </div>

      {/* 긴 제약사·의약품명은 다음 줄로 넘긴다 (옆 열 침범 방지) */}
      <div className="flex w-full min-w-0 items-start gap-2 xl:w-auto xl:flex-[2]">
        <LabeledValue
          label="제약사"
          className="w-[120px] flex-none gap-1 sm:w-[200px]"
          valueClassName={WRAP_VALUE_CLASS}
        >
          {orDash(row.pharmaCompanyName)}
        </LabeledValue>
        <LabeledValue
          label="의약품"
          className="gap-1"
          valueClassName={WRAP_VALUE_CLASS}
        >
          <DrugName drugCode={row.drugCode} drugName={row.drugName} />
        </LabeledValue>
      </div>

      <LabeledValue label="담당자" className="w-full gap-1 xl:w-auto">
        <DotSeparated
          items={[
            <button
              key="company"
              type="button"
              className="cursor-pointer text-fg-accent hover:underline"
              onClick={(event) => {
                event.stopPropagation();
                onSelectUser();
              }}
            >
              {orDash(row.userCompanyName)}
            </button>,
            orDash(row.userRepresentativeName),
          ]}
        />
      </LabeledValue>
    </Card>
  );
}
